import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, formatApiError, downloadSigned } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Plus, FolderOpen, X, Pencil, Trash2, Check } from "lucide-react";
import { PasportToolbar } from "./PasportToolbar";
import { ItemRow } from "./ItemRow";
import { ItemDrawer } from "./ItemDrawer";
import { OrderDialog } from "@/components/orders/OrderDialog";
import { DecorList } from "./DecorList";
import { DecorPicker } from "./DecorPicker";
import { CategorySelect } from "./CategorySelect";
import { CategoryManager } from "./CategoryManager";
import { itemMatches, STATE_OPTS, fmtDate } from "./helpers";
import { ask } from "@/lib/confirm";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || apiError(e));
const orderPayloadFiles = (order) => (order?.files || []).map((f) => ({ file_url: f.url, name: f.name, size: f.size }));

export function PasportTab({ projectId, onCounts }) {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "dekory" ? "dekory" : "pasport";
  const group = params.get("group") === "cat" ? "cat" : "room";
  const fstate = params.get("fstate") || null;
  const fcats = (params.get("fcats") || "").split(",").filter(Boolean);
  const fsups = (params.get("fsups") || "").split(",").filter(Boolean);
  const q = params.get("q") || "";
  const classParam = params.get("class");

  const setParam = useCallback((upd) => {
    const n = new URLSearchParams(params);
    Object.entries(upd).forEach(([k, v]) => { if (v == null || v === "") n.delete(k); else n.set(k, v); });
    setParams(n, { replace: true });
  }, [params, setParams]);

  const [data, setData] = useState(null);
  const [decors, setDecors] = useState([]);
  const [cats, setCats] = useState([]);
  const [qInput, setQInput] = useState(q);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [drawerId, setDrawerId] = useState(null);
  const [orderTarget, setOrderTarget] = useState(null); // 311: { project_id, items } pro novou objednávku
  const [inline, setInline] = useState(null); // {groupId}
  const [inlineName, setInlineName] = useState("");
  const [inlineQty, setInlineQty] = useState("1");
  const [inlineCat, setInlineCat] = useState(() => localStorage.getItem(`pp-pasport-lastcat-${projectId}`) || "");
  const [inlineTried, setInlineTried] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);
  const [bulkDecorOpen, setBulkDecorOpen] = useState(false);
  const [tplSaveOpen, setTplSaveOpen] = useState(false);
  const [tplLoadOpen, setTplLoadOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [catManageOpen, setCatManageOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [closed, setClosed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`pasport-closed-${projectId}`) || "[]")); } catch { return new Set(); }
  });

  const load = useCallback(async () => {
    try {
      const [p, d, c] = await Promise.all([
        api.get(`/projects/${projectId}/pasport`), api.get("/decors"),
        api.get("/pasport-categories").catch(() => ({ data: [] }))]);
      setData(p.data); setDecors(d.data); setCats(c.data || []);
    } catch (e) { err(e); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // debounce hledání do URL
  useEffect(() => { const t = setTimeout(() => { if (qInput !== q) setParam({ q: qInput }); }, 200); return () => clearTimeout(t); }, [qInput]); // eslint-disable-line

  const classes = data?.classes || [];
  const canEdit = !!data?.can_edit;
  const canExport = !!data?.can_export;
  const canVzornik = !!data?.can_vzornik;
  const canDeleteClass = canVzornik; // majitel + asistentka (§15)
  const completed = data?.completed || null;
  const decorById = useMemo(() => Object.fromEntries(decors.map((d) => [d.id, d])), [decors]);

  const activeClass = classes.some((c) => c.id === classParam) ? classParam : classes[0]?.id || null;
  const cls = classes.find((c) => c.id === activeClass) || null;

  const allItems = useMemo(() => classes.flatMap((c) => (c.groups || []).flatMap((g) => g.items || [])), [classes]);
  const filled = allItems.filter((i) => i.dekor_id || i.dekor_text).length;
  const ordered = allItems.filter((i) => i.order).length;
  const usage = useMemo(() => { const u = {}; allItems.forEach((i) => { if (i.dekor_id) u[i.dekor_id] = (u[i.dekor_id] || 0) + 1; }); return u; }, [allItems]);

  useEffect(() => {
    if (!data) return;
    onCounts?.({ total: allItems.length, pending: allItems.filter((i) => !i.order).length });
  }, [data, allItems, onCounts]);

  // Volby filtru z aktivní třídy BEZ ostatních filtrů (§4.4).
  const clsItems = useMemo(() => (cls?.groups || []).flatMap((g) => g.items || []), [cls]);
  const stateCounts = useMemo(() => ({
    nodekor: clsItems.filter((i) => !(i.dekor_id || i.dekor_text)).length,
    todo: clsItems.filter((i) => (i.dekor_id || i.dekor_text) && !i.order).length,
    ordered: clsItems.filter((i) => i.order).length,
    notordered: clsItems.filter((i) => !i.order).length,
  }), [clsItems]);
  const catOpts = useMemo(() => {
    const m = {}; clsItems.forEach((i) => { const k = i.category || "Bez kategorie"; m[k] = (m[k] || 0) + 1; });
    const arr = Object.entries(m).filter(([k]) => k !== "Bez kategorie").sort((a, b) => a[0].localeCompare(b[0], "cs"));
    if (m["Bez kategorie"]) arr.push(["Bez kategorie", m["Bez kategorie"]]);
    return arr;
  }, [clsItems]);
  const supOpts = useMemo(() => {
    const m = {}; clsItems.forEach((i) => { const s = decorById[i.dekor_id]?.supplier; if (s) m[s] = (m[s] || 0) + 1; });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0], "cs"));
  }, [clsItems, decorById]);

  const anyFilter = !!fstate || fcats.length > 0 || fsups.length > 0 || !!q;

  // Skupiny (místnosti / kategorie)
  const groups = useMemo(() => {
    if (!cls) return [];
    if (group === "cat") {
      const m = new Map();
      (cls.groups || []).forEach((g) => (g.items || []).forEach((it) => {
        const k = it.category || "Bez kategorie";
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(it);
      }));
      return [...m.entries()].sort((a, b) => a[0] === "Bez kategorie" ? 1 : b[0] === "Bez kategorie" ? -1 : a[0].localeCompare(b[0], "cs"))
        .map(([name, items]) => ({ key: name, name, gid: null, all: items }));
    }
    return (cls.groups || []).map((g) => ({ key: g.id, name: g.name, gid: g.id, all: g.items || [] }));
  }, [cls, group]);

  const filt = { q, fstate, fcats, fsups };
  const visibleTotal = groups.reduce((s, g) => s + g.all.filter((it) => itemMatches(it, decorById, filt)).length, 0);
  const clsTotal = clsItems.length;

  // ---- mutace ----
  const patchLocal = (iid, patch) => setData((d) => ({ ...d, classes: d.classes.map((c) => ({ ...c,
    groups: (c.groups || []).map((g) => ({ ...g, items: (g.items || []).map((i) => (i.id === iid ? { ...i, ...patch } : i)) })) })) }));
  const saveItem = async (iid, patch) => {
    try { const { data: it } = await api.patch(`/projects/${projectId}/pasport/items/${iid}`, patch); patchLocal(iid, it); }
    catch (e) { err(e); load(); }
  };
  const pickDecor = async (iid, id) => {
    setPickerFor(null);
    const it = allItems.find((x) => x.id === iid);
    const dec = id ? decorById[id] : null;
    const patch = { dekor_id: id || null };
    let filledCat = null;
    // U položky bez kategorie doplníme kategorii z dekoru (jeden PATCH). Vyplněnou nepřepisujeme.
    if (dec?.category && !(it?.category || "").trim()) { patch.category = dec.category; filledCat = dec.category; }
    patchLocal(iid, { ...patch, dekor_text: null });
    await saveItem(iid, patch);
    if (filledCat) toast.success(`Kategorie doplněna podle dekoru: ${filledCat}`);
  };
  const pickCustom = async (iid, { url }) => {
    setPickerFor(null);
    const it = allItems.find((x) => x.id === iid);
    await saveItem(iid, { dekor_text: it?.label, dekor_url: url });
    toast.success("Vlastní odkaz nastaven.");
  };
  const clearCustom = async (iid) => {
    setPickerFor(null);
    await saveItem(iid, { dekor_text: null, dekor_url: null });
  };
  const saveQty = (iid, v) => saveItem(iid, { qty: v === "" ? 1 : v });

  // 311: Objednat = nová objednávka v modulu Objednávky; položky nesou místnost a dekor,
  // stav (číslo, termín, převzetí) se pak do pasportu propisuje sám.
  const startOrder = (ids) => {
    const items = [], sups = new Set();
    classes.forEach((c) => (c.groups || []).forEach((g) => (g.items || []).forEach((it) => {
      if (!ids.includes(it.id) || it.order || !(it.dekor_id || it.dekor_text)) return;
      const dn = decorById[it.dekor_id]?.name || it.dekor_text;
      items.push({ id: it.id, room: g.name, name: [it.label, dn].filter(Boolean).join(" · "), qty: it.qty });
      sups.add(decorById[it.dekor_id]?.supplier || "");
    })));
    if (!items.length) { toast.error("Nic k objednání — vybrané položky už jsou objednané, nebo nemají dekor."); return; }
    // Dodavatel podle vzorníku, když mají všechny položky stejného.
    setOrderTarget({ project_id: projectId, items, supplier: sups.size === 1 ? [...sups][0] : "" });
  };

  const cancelOrder = async (item) => {
    const snap = item.order;
    try {
      const { data: r } = await api.delete(`/projects/${projectId}/pasport/items/${item.id}/order`);
      patchLocal(item.id, r);
      toast.success("Objednávka zrušena.", { duration: 5000, action: { label: "Vrátit", onClick: async () => {
        try { const { data: it } = await api.post(`/projects/${projectId}/pasport/items/${item.id}/order`,
          { order_no: snap.no, order_url: snap.url || undefined, files: orderPayloadFiles(snap) }); patchLocal(item.id, it); }
        catch (e) { err(e); }
      } } });
    } catch (e) { err(e); }
  };

  const recreateItem = async (snap, classId, groupId) => {
    const { data: it } = await api.post(`/projects/${projectId}/pasport/items`,
      { class_id: classId, group_id: groupId, label: snap.label, qty: snap.qty, note: snap.note, category: snap.category });
    if (snap.dekor_id) await api.patch(`/projects/${projectId}/pasport/items/${it.id}`, { dekor_id: snap.dekor_id });
    if (snap.order && !snap.order.order_id) await api.post(`/projects/${projectId}/pasport/items/${it.id}/order`,
      { order_no: snap.order.no, order_url: snap.order.url || undefined, files: orderPayloadFiles(snap.order) });
  };
  const locate = (iid) => { for (const c of classes) for (const g of (c.groups || [])) if ((g.items || []).some((i) => i.id === iid)) return [c.id, g.id]; return [null, null]; };

  const removeItem = async (item) => {
    const [cid, gid] = locate(item.id);
    try {
      await api.delete(`/projects/${projectId}/pasport/items/${item.id}`);
      setDrawerId(null); load();
      toast.success("Položka smazána.", { duration: 5000, action: { label: "Vrátit", onClick: async () => {
        try { await recreateItem(item, cid, gid); load(); } catch (e) { err(e); } } } });
    } catch (e) { err(e); }
  };

  const removeClass = async (c) => {
    const idx = classes.findIndex((x) => x.id === c.id);
    try {
      const { data: r } = await api.delete(`/projects/${projectId}/pasport/classes/${c.id}`);
      if (c.id === activeClass) setParam({ class: null });
      load();
      toast.success(`Třída „${c.name}" smazána.`, { duration: 5000, action: { label: "Vrátit", onClick: async () => {
        try { await api.post(`/projects/${projectId}/pasport/classes/restore`, { class: c, index: r.index ?? idx }); load(); }
        catch (e) { err(e); } } } });
    } catch (e) { err(e); }
  };

  const bulkDelete = async () => {
    const snaps = [...selected].map((iid) => { const [cid, gid] = locate(iid); const it = allItems.find((i) => i.id === iid); return { it, cid, gid }; }).filter((s) => s.it);
    try {
      await Promise.all(snaps.map((s) => api.delete(`/projects/${projectId}/pasport/items/${s.it.id}`)));
      setSelected(new Set()); load();
      toast.success(`Smazáno ${snaps.length} položek.`, { duration: 5000, action: { label: "Vrátit", onClick: async () => {
        try { for (const s of snaps) await recreateItem(s.it, s.cid, s.gid); load(); } catch (e) { err(e); } } } });
    } catch (e) { err(e); }
  };

  const bulkSetDecor = async (dekorId) => {
    setBulkDecorOpen(false);
    try { await Promise.all([...selected].map((iid) => api.patch(`/projects/${projectId}/pasport/items/${iid}`, { dekor_id: dekorId }))); setSelected(new Set()); load(); toast.success("Dekor nastaven."); }
    catch (e) { err(e); }
  };

  const addOrderFile = async (item, f) => {
    try { const { data: it } = await api.post(`/projects/${projectId}/pasport/items/${item.id}/order/files`, f); patchLocal(item.id, it); toast.success("Příloha přidána."); }
    catch (e) { err(e); }
  };
  const deleteOrderFile = async (item, fid) => {
    try { const { data: it } = await api.delete(`/projects/${projectId}/pasport/items/${item.id}/order/files/${fid}`); patchLocal(item.id, it); }
    catch (e) { err(e); }
  };

  // třídy / místnosti / položky
  const addClass = async (name) => {
    try { const { data: c } = await api.post(`/projects/${projectId}/pasport/classes`, { name }); await load(); setParam({ class: c.id }); }
    catch (e) { err(e); }
  };
  const renameClass = async (cid, name) => { try { await api.patch(`/projects/${projectId}/pasport/classes/${cid}`, { name }); load(); } catch (e) { err(e); } };
  const addGroup = async (name) => { try { await api.post(`/projects/${projectId}/pasport/groups`, { class_id: activeClass, name }); load(); } catch (e) { err(e); } };
  const renameGroup = async (gid, name) => { try { await api.patch(`/projects/${projectId}/pasport/groups/${gid}`, { name }); load(); } catch (e) { err(e); } };
  const removeGroup = async (g) => {
    if (!await ask(`Smazat místnost „${g.name}"?`)) return;
    try { await api.delete(`/projects/${projectId}/pasport/groups/${g.id}`); load(); } catch (e) { err(e); }
  };

  const submitInline = async (keepOpen) => {
    const name = inlineName.trim();
    const category = inlineCat.trim();
    if (!name || !category) { setInlineTried(true); return; }
    try {
      await api.post(`/projects/${projectId}/pasport/items`, { class_id: activeClass, group_id: inline.groupId, label: name, qty: inlineQty, category });
      localStorage.setItem(`pp-pasport-lastcat-${projectId}`, category);
      await load();
      setInlineName(""); setInlineQty("1"); setInlineTried(false);  // kategorie zůstává lepivá
      if (!keepOpen) setInline(null);
    } catch (e) { err(e); }
  };
  const startNewItem = () => {
    if (group !== "room") setParam({ group: "room" });
    const gid = cls?.groups?.[0]?.id;
    if (!gid) { toast.error("Nejdřív přidejte místnost."); return; }
    setInline({ groupId: gid }); setInlineName(""); setInlineQty("1"); setInlineTried(false);
  };

  const toggleGroup = (key) => setClosed((s) => { const n = new Set(s); const k = `${activeClass}|${key}`; n.has(k) ? n.delete(k) : n.add(k); localStorage.setItem(`pasport-closed-${projectId}`, JSON.stringify([...n])); return n; });
  const toggleSelect = (iid) => setSelected((s) => { const n = new Set(s); n.has(iid) ? n.delete(iid) : n.add(iid); return n; });

  // šablony
  const openTplLoad = async () => { try { const { data: t } = await api.get("/pasport-templates"); setTemplates(t); setTplLoadOpen(true); } catch (e) { err(e); } };
  const saveTpl = async () => { const name = tplName.trim(); if (!name) return; try { await api.post("/pasport-templates", { name, project_id: projectId }); setTplSaveOpen(false); setTplName(""); toast.success(`Šablona „${name}" uložena.`); } catch (e) { err(e); } };
  const applyTpl = async (t) => {
    if ((filled > 0 || ordered > 0) && !await ask(`Přepíšeš ${filled} vyplněných položek. Pokračovat?`)) return;
    try { const { data: r } = await api.post(`/projects/${projectId}/pasport/apply-template`, { template_id: t.id }); await load(); setParam({ class: r.classes[0]?.id }); setTplLoadOpen(false); toast.success(`Načtena šablona „${r.name}".`); }
    catch (e) { err(e); }
  };

  const completePasport = async () => {
    try { await api.post(`/projects/${projectId}/pasport/complete`); setCompleteOpen(false); await load(); toast.success("Pasportizace dokončena — upozornění odesláno."); }
    catch (e) { err(e); }
  };
  const reopenPasport = async () => {
    if (!await ask("Znovu otevřít pasportizaci? Asistentka i majitel dostanou upozornění.")) return;
    try { await api.post(`/projects/${projectId}/pasport/reopen`); await load(); toast.success("Pasportizace znovu otevřena."); }
    catch (e) { err(e); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Načítám pasport…</p>;

  const drawerItem = drawerId ? allItems.find((i) => i.id === drawerId) : null;
  const [drawerCid, drawerGid] = drawerId ? locate(drawerId) : [null, null];
  const drawerCls = classes.find((c) => c.id === drawerCid);
  const drawerGroupName = drawerCls?.groups?.find((g) => g.id === drawerGid)?.name || "";
  // Hromadné „Nastavit dekor": zúžení jen když všechny vybrané sdílí jednu kategorii.
  const bulkCat = (() => {
    const its = [...selected].map((id) => allItems.find((i) => i.id === id)).filter(Boolean);
    const set = new Set(its.map((i) => (i.category || "").trim().toLowerCase()));
    return its.length > 0 && set.size === 1 && !set.has("") ? its[0].category : null;
  })();
  const bulkMixed = selected.size > 0 && !bulkCat;
  const decorsOk = filled === allItems.length && allItems.length > 0;

  return (
    <div className="bg-card border border-border rounded-[16px] p-4 md:p-5" data-testid="project-pasport-tab-card">
      <datalist id="pasport-cats-list">{cats.map((c) => <option key={c.name} value={c.name} />)}</datalist>

      {/* Hlavička (§3) */}
      <div className="flex items-start gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <h2 className="text-[19px] font-black tracking-tight">Pasportizace</h2>
          {allItems.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap mt-1.5">
              <span data-testid="pasport-decors-chip" className={`rounded-full px-3 py-1 text-[13px] font-bold whitespace-nowrap ${decorsOk ? "bg-success/15 text-success" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
                {decorsOk ? "Dekory vyplněné u všech položek" : `Dekory: ${filled} z ${allItems.length}`}
              </span>
              <span className="inline-flex items-center gap-2" data-testid="pasport-order-progress">
                <span className="w-[76px] h-[6px] rounded-full bg-muted overflow-hidden inline-block">
                  <span className="block h-full bg-success" style={{ width: `${allItems.length ? Math.round(ordered / allItems.length * 100) : 0}%` }} />
                </span>
                <span className="text-[13px] font-semibold text-muted-foreground">Objednáno {ordered} z {allItems.length}</span>
              </span>
            </div>
          )}
        </div>
        <div className="flex-1" />
        {completed ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid="pasport-completed-chip" className="bg-success/15 text-success rounded-full px-3 py-1 text-[13px] font-bold whitespace-nowrap inline-flex items-center gap-1.5">
              <Check size={14} /> Dokončeno {fmtDate((completed.at || "").slice(0, 10))} · {completed.by_name}
            </span>
            {canEdit && (
              <button type="button" onClick={reopenPasport} className="text-[13px] text-muted-foreground hover:text-brand font-semibold" data-testid="pasport-reopen">
                Znovu otevřít
              </button>
            )}
          </div>
        ) : canEdit && (
          <Button variant="outline" onClick={() => setCompleteOpen(true)} className="h-10 font-bold border-success text-success hover:bg-success/10" data-testid="pasport-complete-btn">
            <Check size={16} /> Dokončit pasportizaci
          </Button>
        )}
        {canExport && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="h-10 px-4 inline-flex items-center gap-2 rounded-[11px] border border-border bg-card font-semibold text-sm hover:bg-secondary" data-testid="pasport-more-btn">
              <FolderOpen size={15} /> Šablony a export <ChevronDown size={14} className="opacity-70" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {canEdit && <DropdownMenuItem onClick={openTplLoad} className="flex-col items-start gap-0" data-testid="pasport-tpl-load-btn">
              <span className="font-semibold">Načíst šablonu</span><span className="text-[12px] font-medium text-muted-foreground">nahradí strukturu, dekory zůstanou prázdné</span></DropdownMenuItem>}
            {canVzornik && <DropdownMenuItem onClick={() => { setTplName(""); setTplSaveOpen(true); }} className="flex-col items-start gap-0" data-testid="pasport-tpl-save-btn">
              <span className="font-semibold">Uložit jako šablonu</span><span className="text-[12px] font-medium text-muted-foreground">třídy, místnosti, položky a množství</span></DropdownMenuItem>}
            <DropdownMenuItem onClick={() => downloadSigned(`/api/projects/${projectId}/pasport/export.xlsx`, "pasport.xlsx")} className="flex-col items-start gap-0" data-testid="pasport-export-btn">
              <span className="font-semibold">Export do Excelu</span><span className="text-[12px] font-medium text-muted-foreground">aktuální pohled včetně filtrů</span></DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
        {view === "pasport" && canEdit && cls && (
          <Button onClick={startNewItem} className="h-10 font-bold" data-testid="pasport-add-item-btn"><Plus size={16} /> Nová položka</Button>
        )}
      </div>

      <PasportToolbar
        view={view} setView={(v) => setParam({ view: v })}
        qInput={qInput} setQInput={setQInput}
        classes={classes} activeClass={activeClass} canEdit={canEdit} canDeleteClass={canDeleteClass}
        onSelectClass={(id) => { setParam({ class: id }); setSelected(new Set()); }} onAddClass={addClass} onRenameClass={renameClass} onDeleteClass={removeClass}
        filterOpen={filterOpen} setFilterOpen={setFilterOpen}
        filterProps={{ fstate, fcats, fsups, stateCounts, catOpts, supOpts, group,
          setGroup: (g) => { setParam({ group: g }); setClosed(new Set()); },
          setFstate: (v) => setParam({ fstate: v }),
          toggleCat: (c) => setParam({ fcats: (fcats.includes(c) ? fcats.filter((x) => x !== c) : [...fcats, c]).join(",") }),
          toggleSup: (s) => setParam({ fsups: (fsups.includes(s) ? fsups.filter((x) => x !== s) : [...fsups, s]).join(",") }),
          clearAll: () => setParam({ fstate: null, fcats: null, fsups: null }) }} />

      {view === "dekory" ? (
        <DecorList decors={decors} cats={cats} usage={usage} canVzornik={canVzornik} onChanged={load} onManageCats={() => setCatManageOpen(true)} />
      ) : !cls ? (
        <p className="text-sm text-muted-foreground">Zatím žádná třída. {canEdit ? "Přidej ji v nabídce Bez třídy." : ""}</p>
      ) : (
        <>
          {/* Řádek aktivních omezení (§5) */}
          {anyFilter && (
            <div className="flex items-center gap-2 flex-wrap mt-1 mb-3" data-testid="pasport-active-filters">
              <span className="text-[12px] font-bold tracking-[.06em] text-muted-foreground uppercase">Omezeno na</span>
              {q && <FChip onClear={() => { setQInput(""); }}>„{q}"</FChip>}
              {fstate && <FChip onClear={() => setParam({ fstate: null })}>{STATE_OPTS.find(([v]) => v === fstate)?.[1]}</FChip>}
              {fcats.map((c) => <FChip key={c} onClear={() => setParam({ fcats: fcats.filter((x) => x !== c).join(",") })}>{c}</FChip>)}
              {fsups.map((s) => <FChip key={s} onClear={() => setParam({ fsups: fsups.filter((x) => x !== s).join(",") })}>{s}</FChip>)}
              <button type="button" onClick={() => { setQInput(""); setParam({ q: null, fstate: null, fcats: null, fsups: null }); }} className="text-[13px] font-bold text-brand hover:underline" data-testid="pasport-clear-all">zrušit vše</button>
              <span className="text-[13px] text-muted-foreground ml-auto">{visibleTotal} z {clsTotal} položek</span>
            </div>
          )}

          {groups.length === 0 && <p className="text-sm text-muted-foreground mb-3">Zatím žádná místnost.</p>}

          {anyFilter && visibleTotal === 0 && groups.length > 0 && (
            <div className="border border-dashed border-border rounded-[14px] p-8 text-center" data-testid="pasport-empty-filter">
              <p className="font-bold">Nic neodpovídá filtru</p>
              <p className="text-sm text-muted-foreground mt-1">Ve třídě „{cls.name}" je {clsTotal} položek, žádná nesedí na zvolená omezení.</p>
              <Button variant="outline" onClick={() => { setQInput(""); setParam({ q: null, fstate: null, fcats: null, fsups: null }); }} className="mt-3 font-bold">Zrušit filtry</Button>
            </div>
          )}

          {groups.map((g) => {
            const vis = g.all.filter((it) => itemMatches(it, decorById, filt));
            if (anyFilter && vis.length === 0) return null;
            const gFilled = g.all.filter((i) => i.dekor_id || i.dekor_text).length;
            const gOrdered = g.all.filter((i) => i.order).length;
            const isClosed = closed.has(`${activeClass}|${g.key}`);
            return (
              <div key={g.key} className="border border-border rounded-[14px] mb-3.5 overflow-hidden" data-testid={`pasport-group-${g.key}`}>
                <div role="button" tabIndex={0} onClick={() => toggleGroup(g.key)}
                  className={`flex items-center gap-3 px-4 py-3 bg-secondary cursor-pointer flex-wrap ${isClosed ? "" : "border-b border-border"}`}>
                  <span className="w-[26px] h-[26px] rounded-[8px] border border-border bg-card grid place-items-center text-muted-foreground shrink-0">
                    {isClosed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}</span>
                  <span className="font-extrabold">{g.name}</span>
                  <span className="text-[13px] font-bold text-muted-foreground bg-card border border-border rounded-full px-2.5 py-0.5">
                    {anyFilter ? `${vis.length} z ${g.all.length}` : `${g.all.length} položek`}</span>
                  <div className="flex-1" />
                  {g.all.length > 0 && <span className={`text-[13px] font-bold rounded-full px-2.5 py-0.5 ${gFilled === g.all.length ? "bg-success/15 text-success" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>{gFilled} / {g.all.length} dekorů</span>}
                  {g.all.length > 0 && <span className={`text-[13px] font-bold ${gOrdered === g.all.length ? "text-success" : "text-muted-foreground"}`}>{gOrdered} / {g.all.length} objednáno</span>}
                  {group === "room" && canEdit && (
                    <span className="flex items-center gap-1 shrink-0">
                      <button type="button" title="Přejmenovat místnost" onClick={(e) => { e.stopPropagation(); const nn = window.prompt("Název místnosti", g.name); if (nn && nn.trim()) renameGroup(g.gid, nn.trim()); }} className="w-9 h-9 grid place-items-center rounded-[8px] text-muted-foreground hover:bg-card hover:text-brand" data-testid={`pasport-group-edit-${g.gid}`}><Pencil size={15} /></button>
                      <button type="button" title="Smazat místnost" onClick={(e) => { e.stopPropagation(); removeGroup(g); }} className="w-9 h-9 grid place-items-center rounded-[8px] text-muted-foreground hover:bg-card hover:text-destructive" data-testid={`pasport-group-del-${g.gid}`}><Trash2 size={15} /></button>
                    </span>
                  )}
                </div>
                {!isClosed && (
                  <>
                    {vis.map((it) => (
                      <ItemRow key={it.id} item={it} decor={decorById[it.dekor_id]} canEdit={canEdit} decors={decors}
                        selected={selected.has(it.id)} onToggleSelect={toggleSelect}
                        onPickDecor={pickDecor} onPickCustom={pickCustom} onClearCustom={clearCustom} onQty={saveQty} onOrder={(i) => startOrder([i.id])} onCancelOrder={cancelOrder} onOpenDrawer={setDrawerId}
                        pickerOpen={pickerFor === it.id} setPickerOpen={(v) => setPickerFor(v)} />
                    ))}
                    {vis.length === 0 && !anyFilter && <p className="px-4 py-3 text-sm text-muted-foreground">Zatím žádná položka.</p>}
                    {group === "room" && canEdit && (
                      inline?.groupId === g.gid ? (
                        <div className="flex items-center gap-2 px-3 py-3 bg-amber-500/5 flex-wrap" data-testid="pasport-inline-add">
                          <Input autoFocus value={inlineName} onChange={(e) => setInlineName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") submitInline(true); if (e.key === "Escape") setInline(null); }}
                            placeholder="Co se řeší" className="h-10 flex-1 min-w-[160px] border-brand" data-testid="pasport-inline-name" />
                          <CategorySelect value={inlineCat} cats={cats} canVzornik={canVzornik} onManage={() => setCatManageOpen(true)}
                            onChange={setInlineCat}
                            className={`h-10 w-[160px] ${inlineTried && !inlineCat.trim() ? "border-destructive" : ""}`}
                            testid="pasport-inline-cat" placeholder="Kategorie" />
                          <Input value={inlineQty} onChange={(e) => setInlineQty(e.target.value)} inputMode="decimal" className="h-10 w-[74px] tabular-nums" data-testid="pasport-inline-qty" />
                          <Button onClick={() => submitInline(true)} disabled={!inlineName.trim() || !inlineCat.trim()} className="h-10 font-bold" data-testid="pasport-inline-add-save">Přidat</Button>
                          <Button variant="outline" onClick={() => setInline(null)} className="h-10 font-bold">Zrušit</Button>
                          {inlineTried && !inlineCat.trim()
                            ? <span className="text-[12px] font-semibold text-destructive w-full sm:w-auto">Vyberte kategorii — podle ní se pak nabízí dekory.</span>
                            : <span className="text-[12px] text-muted-foreground w-full sm:w-auto">Enter přidá a nechá řádek otevřený</span>}
                        </div>
                      ) : (
                        <button type="button" onClick={() => { setInline({ groupId: g.gid }); setInlineName(""); setInlineQty("1"); setInlineTried(false); }}
                          className="w-full min-h-[44px] px-4 text-left text-sm font-semibold text-muted-foreground hover:bg-secondary/50" data-testid={`pasport-add-item-${g.gid}`}>
                          + Přidat položku do „{g.name}"
                        </button>
                      )
                    )}
                  </>
                )}
              </div>
            );
          })}

          {group === "room" && canEdit && (
            <button type="button" onClick={() => { const nn = window.prompt("Název místnosti"); if (nn && nn.trim()) addGroup(nn.trim()); }}
              className="min-h-[44px] px-4 rounded-[12px] border border-dashed border-border text-[15px] font-semibold text-muted-foreground hover:bg-secondary" data-testid="pasport-add-group-btn">
              + Podtřída (místnost)
            </button>
          )}
        </>
      )}

      {/* Hromadná lišta (§9) */}
      {view === "pasport" && selected.size > 0 && (
        <div className="fixed lg:sticky bottom-[calc(var(--bottom-nav-h,4.5rem)+env(safe-area-inset-bottom))] lg:bottom-4 left-3 right-3 lg:left-auto lg:right-auto z-40 mt-3 flex items-center gap-2 flex-wrap bg-card border border-border rounded-[16px] px-4 py-2.5 shadow-[0_18px_44px_rgba(0,0,0,0.16)]" data-testid="pasport-bulk-bar">
          <span className="font-bold text-sm">{selected.size} vybraných</span>
          <span className="h-4 w-px bg-border" />
          <Button onClick={() => startOrder([...selected])} className="h-9 font-bold" data-testid="bulk-order">Objednat vybrané</Button>
          <DecorPicker decors={decors} value={null} onPick={bulkSetDecor} preferredCategory={bulkCat}
            mixedMessage={bulkMixed ? "Vybrané položky mají různé kategorie — dekor nastavte po skupinách stejné kategorie." : undefined}
            open={bulkDecorOpen} onOpenChange={setBulkDecorOpen} testid="bulk-decor-pop"
            trigger={<Button variant="outline" className="h-9 font-bold" data-testid="bulk-decor">Nastavit dekor</Button>} />
          <Button variant="outline" onClick={bulkDelete} className="h-9 font-bold text-destructive" data-testid="bulk-delete">Smazat</Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())} className="h-9 font-bold" data-testid="bulk-clear">Zrušit výběr</Button>
        </div>
      )}

      <ItemDrawer item={drawerItem} cls={drawerCls} group={drawerGroupName} decors={decors} cats={cats} canEdit={canEdit} canVzornik={canVzornik} decorById={decorById}
        onClose={() => setDrawerId(null)} onSaveField={saveItem} onPickDecor={pickDecor} onPickCustom={pickCustom} onClearCustom={clearCustom} onManageCats={() => setCatManageOpen(true)}
        onOrder={(i) => startOrder([i.id])} onCancelOrder={cancelOrder} onAddOrderFile={addOrderFile} onDeleteOrderFile={deleteOrderFile} onRemove={removeItem} />

      <OrderDialog open={!!orderTarget} onClose={() => setOrderTarget(null)} pasport={orderTarget}
        onSaved={() => { setSelected(new Set()); load(); }} />

      {/* Dokončení pasportizace (§3.2) */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="rounded-[18px]" data-testid="pasport-complete-dialog">
          <DialogHeader>
            <DialogTitle>Dokončit pasportizaci</DialogTitle>
            <DialogDescription>Asistentka a majitel dostanou upozornění a každá další změna se bude hlásit.</DialogDescription>
          </DialogHeader>
          <div className="rounded-[12px] bg-muted px-3.5 py-3 text-[14px] font-semibold">
            {allItems.length} položek · {allItems.length - filled} bez dekoru · {allItems.length - ordered} neobjednáno
          </div>
          {(allItems.length - filled > 0 || allItems.length - ordered > 0) && (
            <p className="text-[13px] font-semibold text-amber-600 dark:text-amber-400 mt-2" data-testid="pasport-complete-warn">
              Pasport není kompletní — dokončit ho můžete i tak.
            </p>
          )}
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setCompleteOpen(false)} className="h-11 font-bold">Zrušit</Button>
            <Button onClick={completePasport} className="h-11 font-bold bg-success hover:bg-success/90" data-testid="pasport-complete-confirm">Dokončit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Šablony */}
      <Dialog open={tplSaveOpen} onOpenChange={setTplSaveOpen}>
        <DialogContent className="rounded-[18px]" data-testid="pasport-tpl-save-dialog">
          <DialogHeader><DialogTitle>Uložit jako šablonu</DialogTitle><DialogDescription>Uloží se struktura — třídy, místnosti, položky a množství. Dekory a objednávky ne.</DialogDescription></DialogHeader>
          <Label className="text-[13px] font-bold">Název šablony <span className="text-brand">*</span></Label>
          <Input autoFocus value={tplName} onChange={(e) => setTplName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveTpl(); }} placeholder="např. Rekonstrukce bytu 2+kk" className="h-[50px] mt-1 font-semibold" data-testid="pasport-tpl-name" />
          <DialogFooter className="mt-3"><Button variant="outline" onClick={() => setTplSaveOpen(false)} className="h-11 font-bold">Zrušit</Button><Button onClick={saveTpl} disabled={!tplName.trim()} className="h-11 font-bold" data-testid="pasport-tpl-save-confirm">Uložit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={tplLoadOpen} onOpenChange={setTplLoadOpen}>
        <DialogContent className="rounded-[18px]" data-testid="pasport-tpl-load-dialog">
          <DialogHeader><DialogTitle>Načíst šablonu</DialogTitle><DialogDescription>Nahradí současný pasport strukturou ze šablony. Dekory zůstanou prázdné.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {templates.length === 0 && <p className="text-sm text-muted-foreground">Zatím žádná šablona.</p>}
            {templates.map((t) => (
              <button key={t.id} type="button" onClick={() => applyTpl(t)} data-testid={`pasport-tpl-${t.id}`} className="w-full min-h-[60px] px-4 py-3 border border-border rounded-[12px] text-left hover:bg-secondary">
                <div className="font-bold">{t.name}</div><div className="text-[13px] text-muted-foreground">{t.items_count} položek · dekory se nepřenášejí</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <CategoryManager open={catManageOpen} onOpenChange={setCatManageOpen} cats={cats} onChanged={load} />
    </div>
  );
}

function FChip({ children, onClear }) {
  return (
    <span className="min-h-[34px] pl-3.5 pr-2 inline-flex items-center gap-1.5 rounded-full bg-brand text-white font-bold text-[13px]">
      {children}
      <button type="button" onClick={onClear} className="w-[18px] h-[18px] rounded-full bg-white/25 grid place-items-center hover:bg-white/40"><X size={11} /></button>
    </span>
  );
}

